import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  catchError,
  combineLatest,
  finalize,
  first,
  of,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { CompanyShipmentDetails } from 'src/app/core/models/sales/company-shipment-details.interface';
import {
  ShipmentStatus,
  getShipmentStatusBadgeKey,
  getShipmentStatusLabelKey,
} from 'src/app/core/models/sales/shipment-status.type';
import { ShipmentService } from 'src/app/core/services/shipment.service';

interface ShipmentHeaderView {
  number: string;
  shipmentDate: string;
  status: ShipmentStatus;
  statusLabelKey: string;
  statusBadgeKey: 'warning' | 'success';
  customerLabel: string;
  orderLabel: string;
  orderId: string | null;
}

interface ShipmentLineRowView {
  id: string;
  itemLabel: string;
  warehouseLabel: string;
  quantity: number;
  unitOfMeasureId: number;
  baseQuantity: number;
}

interface ShipmentOverviewView {
  details: CompanyShipmentDetails;
  header: ShipmentHeaderView;
  lines: ShipmentLineRowView[];
  canMarkShipped: boolean;
  canDelete: boolean;
}

@Component({
  selector: 'app-shipment-overview',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, CoreModule],
  templateUrl: './shipment-overview.component.html',
  styleUrl: './shipment-overview.component.scss',
})
export class ShipmentOverviewComponent implements OnDestroy {
  public companyId = '';
  public shipmentId = '';

  public readonly view$ = new BehaviorSubject<ShipmentOverviewView | null>(null);

  public isLoading = false;
  public isMutating = false;
  public errorMessageKey = '';
  public actionErrorMessageKey = '';

  public isMarkShippedModalOpen = false;
  public isDeleteModalOpen = false;

  private readonly unsubscribe$ = new Subject<void>();

  private readonly shipmentService = inject(ShipmentService);
  private readonly router = inject(Router);

  constructor() {
    const route = inject(ActivatedRoute);
    const parentParams$: Observable<Record<string, string>> =
      route.parent?.parent?.params ?? of<Record<string, string>>({});
    combineLatest([parentParams$, route.params])
      .pipe(
        takeUntil(this.unsubscribe$),
        first(),
        tap(([parentParams, params]) => {
          this.companyId = (parentParams as Record<string, string>)['id'];
          this.shipmentId = params['shipmentId'];
          this.loadShipment();
        }),
      )
      .subscribe();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public retry(): void {
    this.loadShipment();
  }

  public openMarkShippedModal(): void {
    this.actionErrorMessageKey = '';
    this.isMarkShippedModalOpen = true;
  }

  public closeMarkShippedModal(): void {
    if (this.isMutating) {
      return;
    }
    this.isMarkShippedModalOpen = false;
  }

  public confirmMarkShipped(): void {
    if (this.isMutating || !this.companyId || !this.shipmentId) {
      return;
    }
    this.isMutating = true;
    this.actionErrorMessageKey = '';

    this.shipmentService
      .markShipped(this.companyId, this.shipmentId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((shipment) => {
          this.applyShipment(shipment);
          this.isMarkShippedModalOpen = false;
        }),
        catchError((error: HttpErrorResponse) => {
          this.actionErrorMessageKey = this.mapShipmentErrorKey(
            error,
            'COMPONENTS.SHIPMENTS.ERRORS.UPDATE_FAILED',
          );
          return EMPTY;
        }),
        finalize(() => {
          this.isMutating = false;
        }),
      )
      .subscribe();
  }

  public openDeleteModal(): void {
    this.actionErrorMessageKey = '';
    this.isDeleteModalOpen = true;
  }

  public closeDeleteModal(): void {
    if (this.isMutating) {
      return;
    }
    this.isDeleteModalOpen = false;
  }

  public confirmDelete(): void {
    if (this.isMutating || !this.companyId || !this.shipmentId) {
      return;
    }
    this.isMutating = true;
    this.actionErrorMessageKey = '';

    this.shipmentService
      .deleteShipment(this.companyId, this.shipmentId)
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(() => {
          this.isDeleteModalOpen = false;
          return this.router.navigate(['/companies', this.companyId, 'crm', 'shipments']);
        }),
        catchError((error: HttpErrorResponse) => {
          this.actionErrorMessageKey = this.mapShipmentErrorKey(
            error,
            'COMPONENTS.SHIPMENTS.ERRORS.DELETE_FAILED',
          );
          return EMPTY;
        }),
        finalize(() => {
          this.isMutating = false;
        }),
      )
      .subscribe();
  }

  private loadShipment(): void {
    if (!this.companyId || !this.shipmentId) {
      return;
    }

    this.isLoading = true;
    this.errorMessageKey = '';

    this.shipmentService
      .getShipmentById(this.companyId, this.shipmentId)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((shipment) => this.applyShipment(shipment)),
        catchError(() => {
          this.errorMessageKey = 'COMPONENTS.SHIPMENTS.ERRORS.LOAD_DETAIL_FAILED';
          this.view$.next(null);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private applyShipment(details: CompanyShipmentDetails): void {
    this.view$.next(this.buildView(details));
  }

  private buildView(details: CompanyShipmentDetails): ShipmentOverviewView {
    const status: ShipmentStatus = details.status === 'Shipped' ? 'Shipped' : 'Packed';
    const header: ShipmentHeaderView = {
      number: details.number || '—',
      shipmentDate: details.shipmentDate || '',
      status,
      statusLabelKey: getShipmentStatusLabelKey(status),
      statusBadgeKey: getShipmentStatusBadgeKey(status),
      customerLabel: details.customerId ? this.shortId(details.customerId) : '—',
      orderLabel: details.salesOrderId ? this.shortId(details.salesOrderId) : '—',
      orderId: details.salesOrderId,
    };

    const lines: ShipmentLineRowView[] = (details.lines ?? []).map((line) => ({
      id: line.id,
      itemLabel: line.inventoryItemId ? this.shortId(line.inventoryItemId) : '—',
      warehouseLabel: line.warehouseId ? this.shortId(line.warehouseId) : '—',
      quantity: line.quantity,
      unitOfMeasureId: line.unitOfMeasureId,
      baseQuantity: line.baseQuantity,
    }));

    return {
      details,
      header,
      lines,
      canMarkShipped: status === 'Packed',
      canDelete: status === 'Packed',
    };
  }

  private shortId(value: string): string {
    return value.length > 8 ? `${value.slice(0, 8)}…` : value;
  }

  private readonly errorCodeKeyMap: Readonly<Record<string, string>> = {
    SHIPMENT_INVALID_STATUS: 'COMPONENTS.SHIPMENTS.ERRORS.SHIPMENT_INVALID_STATUS',
    SHIPMENT_TERMINAL: 'COMPONENTS.SHIPMENTS.ERRORS.SHIPMENT_TERMINAL',
    INSUFFICIENT_STOCK: 'COMPONENTS.SHIPMENTS.ERRORS.INSUFFICIENT_STOCK',
    ORDER_TERMINAL: 'COMPONENTS.SHIPMENTS.ERRORS.ORDER_TERMINAL',
    ORDER_INVALID_STATUS: 'COMPONENTS.SHIPMENTS.ERRORS.ORDER_INVALID_STATUS',
  };

  private mapShipmentErrorKey(error: HttpErrorResponse, fallback: string): string {
    if (error.status === 400 || error.status === 409 || error.status === 422) {
      const code = (error.error as { code?: string } | null)?.code;
      if (code && this.errorCodeKeyMap[code]) {
        return this.errorCodeKeyMap[code];
      }
    }
    return fallback;
  }
}
